import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { format } from 'date-fns';
import { collection, query, where, orderBy, onSnapshot } from 'firebase/firestore';
import { db } from '../../firebaseClient';
import Icon from 'components/AppIcon';

const EditHistory = ({ itemId, itemType }) => {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);

  // Load edit history from Firestore
  useEffect(() => {
    if (!itemId || !itemType) return;

    const q = query(
      collection(db, 'edit_history'),
      where('item_id', '==', itemId),
      where('item_type', '==', itemType),
      orderBy('created_at', 'desc')
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const historyData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        created_at: doc.data().created_at?.toDate() || new Date()
      }));
      setHistory(historyData);
      setLoading(false);
    });

    return () => unsubscribe();
  }, [itemId, itemType]);

  const getFieldDisplayName = (field) => {
    const fieldNames = {
      'assignment_title': 'Assignment Title',
      'class_title': 'Class Title',
      'subject': 'Subject',
      'assignment_type': 'Assignment Type',
      'pages': 'Pages',
      'deadline': 'Deadline',
      'status': 'Status',
      'requirements': 'Requirements',
      'additional_notes': 'Additional Notes',
      'qa_review_notes': 'QA Review Notes',
      'preferred_communication': 'Communication Type',
      'assigned_writer': 'Assigned Writer',
      'class_forum': 'Class Forum',
      'start_date': 'Start Date',
      'end_date': 'End Date',
      'description': 'Description',
      'notes': 'Notes',
      'meeting_link': 'Meeting Link'
    };
    return fieldNames[field] || field;
  };

  const getChangeType = (oldValue, newValue) => {
    if (!oldValue && newValue) return 'added';
    if (oldValue && !newValue) return 'removed';
    if (oldValue && newValue) return 'updated';
    return 'unknown';
  };

  const getChangeTypeIcon = (changeType) => {
    switch (changeType) {
      case 'added': return 'Plus';
      case 'removed': return 'Minus';
      case 'updated': return 'Edit';
      default: return 'Clock';
    }
  };

  const getChangeTypeColor = (changeType) => {
    switch (changeType) {
      case 'added': return 'text-green-400';
      case 'removed': return 'text-red-400';
      case 'updated': return 'text-blue-400';
      default: return 'text-gray-400';
    }
  };

  const formatValue = (value) => {
    if (!value) return 'Empty';
    if (typeof value === 'string' && value.length > 50) {
      return `${value.substring(0, 50)}...`;
    }
    return value.toString();
  };

  if (loading) {
    return (
      <div className="glass-card p-4 rounded-lg">
        <div className="flex items-center space-x-2">
          <Icon name="History" size={20} className="text-white/60" />
          <span className="text-white/60">Loading edit history...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="glass-card p-4 rounded-lg space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-2">
          <Icon name="History" size={20} className="text-white/80" />
          <h3 className="text-white font-medium">Edit History</h3>
          <span className="text-white/60 text-sm">({history.length})</span>
        </div>
        
        {history.length > 0 && (
          <button
            onClick={() => setExpanded(!expanded)}
            className="flex items-center space-x-1 text-white/60 hover:text-white/80 transition-colors"
          >
            <span className="text-sm">{expanded ? 'Show Less' : 'Show All'}</span>
            <Icon 
              name={expanded ? 'ChevronUp' : 'ChevronDown'} 
              size={16} 
            />
          </button>
        )}
      </div>

      {/* History List */}
      <div className="space-y-2">
        <AnimatePresence>
          {history.length === 0 ? (
            <div className="text-center py-8">
              <Icon name="History" size={48} className="text-white/20 mx-auto mb-2" />
              <p className="text-white/60">No edit history yet</p>
              <p className="text-white/40 text-sm">Changes will appear here</p>
            </div>
          ) : (
            history.slice(0, expanded ? history.length : 5).map((entry) => {
              const changeType = getChangeType(entry.old_value, entry.new_value);
              
              return (
                <motion.div
                  key={entry.id}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 20 }}
                  className="bg-white/5 rounded-lg p-3 border border-white/10"
                >
                  <div className="flex items-start space-x-3">
                    <div className="w-8 h-8 bg-white/10 rounded-full flex items-center justify-center">
                      <Icon 
                        name={getChangeTypeIcon(changeType)} 
                        size={14} 
                        className={getChangeTypeColor(changeType)} 
                      />
                    </div>
                    
                    <div className="flex-1">
                      <div className="flex items-center space-x-2 mb-1">
                        <span className="text-white font-medium text-sm">
                          {entry.editor_name}
                        </span>
                        <span className="text-white/60 text-xs">
                          {changeType} {getFieldDisplayName(entry.field)}
                        </span>
                        <span className="text-white/60 text-xs">
                          {format(entry.created_at, 'MMM dd, HH:mm')}
                        </span>
                      </div>
                      
                      <div className="space-y-1">
                        {entry.old_value && (
                          <div className="text-xs">
                            <span className="text-red-400">From:</span>
                            <span className="text-white/60 ml-2">{formatValue(entry.old_value)}</span>
                          </div>
                        )}
                        {entry.new_value && (
                          <div className="text-xs">
                            <span className="text-green-400">To:</span>
                            <span className="text-white/80 ml-2">{formatValue(entry.new_value)}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </motion.div>
              );
            })
          )}
        </AnimatePresence>
        
        {!expanded && history.length > 5 && (
          <button
            onClick={() => setExpanded(true)}
            className="w-full text-center py-2 text-white/60 hover:text-white/80 transition-colors text-sm"
          >
            Show {history.length - 5} more entries...
          </button>
        )}
      </div>
    </div>
  );
};

export default EditHistory;